import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Users, DollarSign, TrendingUp, Copy, LogOut, History, BarChart3, QrCode, Loader2, Eye, EyeOff, MessageSquare, Share2, Gamepad2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import AmbassadorPayDayCard from '../components/affiliate/AmbassadorPayDayCard';
import AmbassadorCupCard from '../components/affiliate/AmbassadorCupCard';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AffiliateDashboard() {
    const navigate = useNavigate();
    const { logout: authLogout } = useAuth();
    const [me, setMe] = useState(null);
    const [referrals, setReferrals] = useState(null);
    const [history, setHistory] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pwOpen, setPwOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async (silent = false) => {
            if (!silent) setLoading(true);
            try {
                const [meRes, refRes, histRes, payRes, previewRes] = await Promise.all([
                    axios.get(`${API}/ambassador/me`, { withCredentials: true }),
                    axios.get(`${API}/ambassador/referrals`, { withCredentials: true }),
                    axios.get(`${API}/ambassador/commission-history`, { withCredentials: true }),
                    axios.get(`${API}/ambassador/payout-history`, { withCredentials: true }),
                    axios.get(`${API}/ambassador/commission-preview`, { withCredentials: true }).catch(() => ({ data: null })),
                ]);
                if (cancelled) return;
                setMe(meRes.data);
                setReferrals(refRes.data);
                setHistory(histRes.data.entries || []);
                setPayouts(payRes.data.payouts || []);
                setPreview(previewRes.data);
            } catch (err) {
                if (err.response?.status === 401) {
                    navigate('/login');
                    return;
                }
                if (!silent) toast.error(err.response?.data?.detail || 'Failed to load dashboard');
            } finally { if (!cancelled) setLoading(false); }
        };
        load();
        // Keep balance / paid fresh so an approved cash-out shows without a reload.
        const iv = setInterval(() => load(true), 20000);
        return () => { cancelled = true; clearInterval(iv); };
    }, [navigate]);

    const logout = async () => {
        // FIX (2026-02-26 — ambassador reported "can't sign out"):
        // The previous impl only hit `/auth/logout` on the backend and
        // navigated to `/login`, but it left localStorage tokens + the
        // AuthContext `user` intact. Login.js (with the iter-171 already-
        // authed redirect) then bounced them straight back to /affiliate,
        // creating an infinite loop.
        // useAuth().logout() clears both localStorage tokens AND flips
        // `user` to false so the /login redirect no longer fires.
        try { await authLogout(); } catch { /* ignore */ }
        navigate('/login', { replace: true });
    };

    const copy = (text, label) => {
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard`);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-zinc-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading your affiliate dashboard…
            </div>
        );
    }

    if (!me) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-zinc-400">
                Unable to load profile. <button onClick={() => navigate('/login')} className="ml-2 text-amber-400 underline">Sign in</button>
            </div>
        );
    }

    const code = me.code;
    const shareLink = typeof window !== 'undefined'
        ? `${window.location.origin}/api/r/${encodeURIComponent(code)}`
        : '';
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&format=svg&margin=10&data=${encodeURIComponent(shareLink)}`;

    // Pre-fab text-message invite. Kept short + friendly. Emoji-light so
    // Android SMS doesn't switch to MMS and cost the ambassador extra.
    const inviteText = `Hey — come play with me on BETohalics! Sportsbook, lottery & slots, all in one. Sign up with my link and we're both in: ${shareLink}`;
    const smsHref = `sms:?&body=${encodeURIComponent(inviteText)}`;

    const inviteViaText = () => {
        // Prefer the OS share sheet on mobile — lets the user text, WhatsApp,
        // iMessage, whatever their contact is on. Falls back to sms: on
        // desktop and finally to copy-to-clipboard.
        if (typeof navigator !== 'undefined' && navigator.share) {
            navigator.share({ title: 'Join me on BETohalics', text: inviteText })
                .catch(() => { /* user dismissed — no-op */ });
            return;
        }
        try {
            window.location.href = smsHref;
        } catch {
            copy(inviteText, 'Invite message');
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-zinc-100" data-testid="affiliate-dashboard">
            {/* Header */}
            <header className="border-b border-zinc-800 bg-[#121212] px-6 py-4 flex items-center justify-between">
                <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Affiliate portal</p>
                    <h1 className="text-2xl font-bold text-white">Hey, {me.name?.split(' ')[0] || 'Partner'}</h1>
                </div>
                <div className="flex items-center gap-2">
                    <Button onClick={() => navigate('/lobby')}
                        className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
                        data-testid="affiliate-enter-casino">
                        <Gamepad2 className="w-4 h-4 mr-1" /> Enter Casino
                    </Button>
                    <Button onClick={() => setPwOpen(true)} variant="outline"
                        className="border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                        data-testid="affiliate-change-pw">
                        Change password
                    </Button>
                    <Button onClick={logout} variant="outline"
                        className="border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                        data-testid="affiliate-logout">
                        <LogOut className="w-4 h-4 mr-1" /> Sign out
                    </Button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-6 space-y-6">
                {/* PAY DAY — top-of-page weekly cashout card. Pulses when the
                    24-hour Monday window is open, locked w/ countdown otherwise. */}
                <AmbassadorPayDayCard />

                {/* Stats row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatTile icon={Users} label="Referrals" value={referrals?.totals.count ?? 0} tone="white" testid="stat-referrals" />
                    <StatTile icon={TrendingUp} label="Lifetime earned" value={fmt(me.total_earned)} tone="amber" testid="stat-earned" />
                    <StatTile icon={DollarSign} label="Pending payout" value={fmt(me.pending_balance)} tone="emerald" testid="stat-pending" />
                    <StatTile icon={BarChart3} label="Commission rate" value={`${me.commission_pct}%`} tone="zinc" testid="stat-pct" />
                </div>

                {/* Real-time uncredited pending — money you've earned since the last commission run */}
                {preview && Number(preview.total_pending) > 0 && (
                    <div className="rounded-lg border border-emerald-800/60 bg-gradient-to-br from-emerald-900/25 to-transparent p-4 flex flex-wrap items-center gap-4" data-testid="stat-uncredited-preview">
                        <div className="flex-1 min-w-[220px]">
                            <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-black mb-1">
                                Estimated pending · not yet credited
                            </p>
                            <p className="text-3xl font-black font-mono text-emerald-300" data-testid="stat-uncredited-total">
                                {fmt(preview.total_pending)}
                            </p>
                            <p className="text-[11px] text-zinc-400 mt-1">
                                Across <b className="text-white">{preview.player_count}</b> player{preview.player_count === 1 ? '' : 's'}.
                                Rolls into your Pending Payout on the next commission post.
                            </p>
                        </div>
                        {preview.players?.some((p) => Number(p.pending_commission) > 0) && (
                            <details className="w-full">
                                <summary className="text-[11px] text-emerald-400 cursor-pointer hover:text-emerald-300">
                                    Show per-player breakdown
                                </summary>
                                <table className="mt-2 w-full text-[11px]">
                                    <thead className="text-[9px] uppercase text-zinc-500">
                                        <tr><th className="text-left py-1">Player</th><th className="text-right">Net profit</th><th className="text-right">Pending</th></tr>
                                    </thead>
                                    <tbody>
                                        {preview.players.filter((p) => Number(p.pending_commission) > 0).map((p, i) => (
                                            <tr key={i} className="border-t border-zinc-800">
                                                <td className="py-1 text-white">{p.player_name || '—'}</td>
                                                <td className="text-right font-mono text-zinc-400">{fmt(p.lifetime_net_house_profit)}</td>
                                                <td className="text-right font-mono font-black text-emerald-300">{fmt(p.pending_commission)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </details>
                        )}
                    </div>
                )}

                {/* Player-money flow — transparent view of what referred players have put in vs. gotten out */}
                <div className="rounded-lg border border-zinc-800 bg-[#0e0e0e] p-4">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-3">
                        Player money flow — all your referrals combined
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatTile icon={TrendingUp} label="They put in" value={fmt(referrals?.totals.total_loaded ?? 0)} tone="white" testid="stat-total-loaded" />
                        <StatTile icon={DollarSign} label="They got out" value={fmt(referrals?.totals.total_cashed_out ?? 0)} tone="zinc" testid="stat-total-cashed" />
                        <StatTile icon={BarChart3} label="Net house profit" value={fmt(referrals?.totals.total_house_profit ?? 0)} tone="emerald" testid="stat-net-profit" />
                        <StatTile icon={TrendingUp} label="Your commission" value={fmt(referrals?.totals.total_commission_earned ?? 0)} tone="amber" testid="stat-your-commission" />
                    </div>
                </div>

                {/* Code + share + QR */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2 rounded-lg border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-transparent to-transparent p-5">
                        <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-1">Your code</p>
                        <div className="flex items-center gap-3 mb-4">
                            <span className="text-3xl font-black text-white font-mono tracking-wider"
                                data-testid="affiliate-code">{code}</span>
                            <button onClick={() => copy(code, 'Code')}
                                className="text-amber-400 hover:text-amber-200 p-1"
                                data-testid="affiliate-copy-code">
                                <Copy className="w-4 h-4" />
                            </button>
                        </div>
                        <Label className="text-[10px] uppercase text-zinc-500">Share link</Label>
                        <div className="flex gap-2 mt-1">
                            <Input readOnly value={shareLink}
                                className="bg-zinc-900 border-zinc-700 text-zinc-200 font-mono text-xs"
                                data-testid="affiliate-share-link" />
                            <Button onClick={() => copy(shareLink, 'Link')}
                                className="bg-amber-500 hover:bg-amber-400 text-zinc-900"
                                data-testid="affiliate-copy-link">
                                <Copy className="w-4 h-4 mr-1" /> Copy
                            </Button>
                        </div>

                        {/* One-tap invite via SMS (opens Messages app with a pre-written
                            invite already in the body). On mobile browsers with the Web
                            Share API this uses the OS share sheet instead. */}
                        <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-1">
                                Invite by text
                            </p>
                            <p className="text-xs text-zinc-400 mb-2 leading-snug italic">
                                &ldquo;{inviteText}&rdquo;
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    onClick={inviteViaText}
                                    className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold"
                                    data-testid="affiliate-invite-text-btn"
                                >
                                    <MessageSquare className="w-4 h-4 mr-1" /> Text a friend
                                </Button>
                                <a
                                    href={smsHref}
                                    className="inline-flex items-center justify-center rounded-md border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 px-3 py-1.5 text-sm font-semibold"
                                    data-testid="affiliate-sms-link"
                                >
                                    <Share2 className="w-4 h-4 mr-1" /> Open Messages
                                </a>
                                <Button
                                    onClick={() => copy(inviteText, 'Invite message')}
                                    variant="outline"
                                    className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
                                    data-testid="affiliate-copy-invite-btn"
                                >
                                    <Copy className="w-4 h-4 mr-1" /> Copy text
                                </Button>
                            </div>
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-3">
                            You earn <span className="text-amber-300 font-bold">{me.commission_pct}%</span> of the
                            house&apos;s net profit from every player you refer — credited daily at midnight Central.
                            No commission is earned on free play, daily bonuses, or other promotional credits.
                        </p>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-[#121212] p-4 flex flex-col items-center justify-center">
                        <QrCode className="w-3.5 h-3.5 text-amber-400 mb-1" />
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">QR code</p>
                        <img src={qrUrl} alt={`QR code for ${code}`}
                            className="w-44 h-44 rounded bg-white p-1.5"
                            data-testid="affiliate-qr-code" />
                        <a href={qrUrl} target="_blank" rel="noopener noreferrer"
                            className="text-[10px] text-amber-400 hover:text-amber-200 mt-2">
                            Download / open
                        </a>
                        {/* The actual link, right under the QR, so it can be read &
                            copied without scanning. */}
                        <div className="w-full mt-3 pt-3 border-t border-zinc-800">
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 text-center">Your link</p>
                            <p className="text-[11px] text-zinc-300 font-mono break-all text-center leading-snug"
                                data-testid="affiliate-qr-link-text">{shareLink}</p>
                            <Button onClick={() => copy(shareLink, 'Link')}
                                className="w-full mt-2 bg-amber-500 hover:bg-amber-400 text-zinc-900 h-8"
                                data-testid="affiliate-qr-copy-link">
                                <Copy className="w-4 h-4 mr-1" /> Copy link
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <Tabs defaultValue="cup" className="w-full">
                    <TabsList className="bg-[#121212] border border-zinc-800">
                        <TabsTrigger value="cup" data-testid="tab-cup">🏆 Cup</TabsTrigger>
                        <TabsTrigger value="referrals" data-testid="tab-referrals">Referrals</TabsTrigger>
                        <TabsTrigger value="commission" data-testid="tab-commission">Commission history</TabsTrigger>
                        <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
                    </TabsList>

                    <TabsContent value="cup">
                        <AmbassadorCupCard />
                    </TabsContent>
                    <TabsContent value="referrals">
                        <ReferralsTable rows={referrals?.referrals || []} />
                    </TabsContent>
                    <TabsContent value="commission">
                        <CommissionHistoryTable rows={history} />
                    </TabsContent>
                    <TabsContent value="payouts">
                        <PayoutsTable rows={payouts} />
                    </TabsContent>
                </Tabs>
            </main>

            <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
        </div>
    );
}

const StatTile = ({ icon: Icon, label, value, tone, testid }) => {
    const tones = {
        amber: 'text-amber-300',
        emerald: 'text-emerald-300',
        zinc: 'text-zinc-200',
        white: 'text-white',
    };
    return (
        <div className="rounded-lg border border-zinc-800 bg-[#121212] p-4" data-testid={testid}>
            <div className="flex items-center gap-2 text-zinc-500 mb-1.5">
                <Icon className="w-3.5 h-3.5" />
                <span className="text-[10px] uppercase tracking-widest font-semibold">{label}</span>
            </div>
            <p className={`font-mono text-2xl font-bold ${tones[tone] || 'text-white'}`}>{value}</p>
        </div>
    );
};

const ReferralsTable = ({ rows }) => {
    if (rows.length === 0) {
        return (
            <div className="rounded-lg border border-zinc-800 bg-[#121212] p-10 text-center">
                <Users className="w-8 h-8 mx-auto text-zinc-700 mb-2" />
                <p className="text-zinc-400">No referrals yet.</p>
                <p className="text-xs text-zinc-500 mt-1">Share your code or QR with friends to start earning.</p>
            </div>
        );
    }
    return (
        <div className="rounded-lg border border-zinc-800 bg-[#121212] overflow-hidden">
            <div className="px-4 py-2 text-[11px] text-zinc-400 border-b border-zinc-800" data-testid="earnings-breakdown-note">
                How you earn: <span className="text-zinc-200">Deposited − Cashed out − Still in balance = Player lost</span>. You earn your % only on what a player has actually lost. Money still in their balance or being cashed out earns nothing, and bonus or free-play money doesn't count.
            </div>
            <Table>
                <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-[10px] uppercase">Player</TableHead>
                        <TableHead className="text-[10px] uppercase">Email</TableHead>
                        <TableHead className="text-[10px] uppercase">Phone</TableHead>
                        <TableHead className="text-[10px] uppercase">Joined</TableHead>
                        <TableHead className="text-[10px] uppercase">Status</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Deposited</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Cashed out<div className="normal-case text-[9px] text-zinc-500">incl. requested</div></TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Still in balance</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Player lost<div className="normal-case text-[9px] text-zinc-500">you earn on this</div></TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Your commission</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r, i) => (
                        <TableRow key={r.id} className="border-zinc-800 hover:bg-zinc-900/40"
                            data-testid={`affiliate-referral-row-${i}`}>
                            <TableCell className="text-sm text-zinc-200">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span>{r.player_name || '—'}</span>
                                    {r.chain_depth > 0 && (
                                        <span
                                            data-testid={`affiliate-chain-badge-${i}`}
                                            title={r.via_referrer_name ? `Joined via ${r.via_referrer_name}` : 'Chained referral'}
                                            className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30"
                                        >
                                            Tier {r.chain_depth + 1}
                                        </span>
                                    )}
                                    {r.manually_linked_at && (
                                        <span
                                            className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30"
                                            title="Manually linked by admin — you get credit going forward + retro"
                                        >
                                            Retro
                                        </span>
                                    )}
                                </div>
                            </TableCell>
                            <TableCell className="text-xs text-zinc-300 break-all">
                                {r.player_email ? (
                                    <a href={`mailto:${r.player_email}`} className="hover:text-amber-300 transition-colors" data-testid={`affiliate-referral-email-${i}`}>
                                        {r.player_email}
                                    </a>
                                ) : '—'}
                            </TableCell>
                            <TableCell className="text-xs font-mono text-zinc-400">
                                {r.player_phone ? (
                                    <a href={`tel:${r.player_phone}`} className="hover:text-amber-300 transition-colors" data-testid={`affiliate-referral-phone-${i}`}>
                                        {r.player_phone}
                                    </a>
                                ) : '—'}
                            </TableCell>
                            <TableCell className="text-xs text-zinc-400">
                                {r.joined_at ? new Date(r.joined_at).toLocaleDateString() : '—'}
                            </TableCell>
                            <TableCell>
                                <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${
                                    r.status === 'active' ? 'bg-emerald-500/10 text-emerald-300'
                                    : r.status === 'cancelled' ? 'bg-rose-500/10 text-rose-300'
                                    : 'bg-zinc-700/40 text-zinc-300'}`}>
                                    {r.status}
                                </span>
                            </TableCell>
                            <TableCell className="text-right font-mono text-zinc-200">{fmt(r.lifetime_loaded)}</TableCell>
                            <TableCell className="text-right font-mono text-zinc-400">{fmt(r.lifetime_cashed_out)}</TableCell>
                            <TableCell className="text-right font-mono text-zinc-400">{fmt(r.current_balance)}</TableCell>
                            <TableCell className={`text-right font-mono ${r.lifetime_net_house_profit >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{fmt(r.lifetime_net_house_profit)}</TableCell>
                            <TableCell className="text-right font-mono text-amber-300 font-bold">{fmt(r.lifetime_commission_earned)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};

const CommissionHistoryTable = ({ rows }) => {
    if (rows.length === 0) {
        return (
            <div className="rounded-lg border border-zinc-800 bg-[#121212] p-10 text-center">
                <History className="w-8 h-8 mx-auto text-zinc-700 mb-2" />
                <p className="text-zinc-400">No commission entries yet.</p>
                <p className="text-xs text-zinc-500 mt-1">Daily commissions appear here after midnight CT.</p>
            </div>
        );
    }
    return (
        <div className="rounded-lg border border-zinc-800 bg-[#121212] overflow-hidden">
            <Table>
                <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-[10px] uppercase">Date</TableHead>
                        <TableHead className="text-[10px] uppercase">Player</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Profit Δ</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Rate</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Commission</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r, i) => (
                        <TableRow key={r.id} className="border-zinc-800 hover:bg-zinc-900/40"
                            data-testid={`affiliate-commission-row-${i}`}>
                            <TableCell className="text-xs text-zinc-400 font-mono">{r.date}</TableCell>
                            <TableCell className="text-sm text-zinc-200">{r.player_name || '—'}</TableCell>
                            <TableCell className="text-right font-mono text-zinc-300">{fmt(r.delta)}</TableCell>
                            <TableCell className="text-right font-mono text-zinc-400">{r.commission_pct}%</TableCell>
                            <TableCell className="text-right font-mono text-amber-300 font-bold">{fmt(r.commission)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};

const PayoutsTable = ({ rows }) => {
    if (rows.length === 0) {
        return (
            <div className="rounded-lg border border-zinc-800 bg-[#121212] p-10 text-center">
                <DollarSign className="w-8 h-8 mx-auto text-zinc-700 mb-2" />
                <p className="text-zinc-400">No payouts yet.</p>
                <p className="text-xs text-zinc-500 mt-1">Pending balance accumulates weekly; admin marks payouts here.</p>
            </div>
        );
    }
    return (
        <div className="rounded-lg border border-zinc-800 bg-[#121212] overflow-hidden">
            <Table>
                <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-[10px] uppercase">Date</TableHead>
                        <TableHead className="text-[10px] uppercase">Method</TableHead>
                        <TableHead className="text-[10px] uppercase">Notes</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Amount</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((p, i) => (
                        <TableRow key={p.id} className="border-zinc-800 hover:bg-zinc-900/40"
                            data-testid={`affiliate-payout-row-${i}`}>
                            <TableCell className="text-xs text-zinc-400">
                                {p.created_at ? new Date(p.created_at).toLocaleString() : '—'}
                            </TableCell>
                            <TableCell className="text-sm text-zinc-200 capitalize">{p.method || '—'}</TableCell>
                            <TableCell className="text-xs text-zinc-500 max-w-[300px] truncate">{p.notes || '—'}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-300 font-bold">{fmt(p.amount)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};

const ChangePasswordDialog = ({ open, onClose }) => {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (next.length < 6) { toast.error('New password must be at least 6 characters'); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/ambassador/change-password`,
                { current_password: current, new_password: next },
                { withCredentials: true });
            toast.success('Password updated');
            setCurrent(''); setNext('');
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Update failed');
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="bg-[#121212] border-white/10 max-w-sm">
                <DialogHeader>
                    <DialogTitle className="text-white">Change password</DialogTitle>
                    <DialogDescription className="text-[#A0AAB2]">
                        Use a strong password — at least 6 characters.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                    <div>
                        <Label className="text-xs text-zinc-400">Current password</Label>
                        <Input type={show ? 'text' : 'password'} value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            className="mt-1 bg-zinc-900 border-zinc-700 text-zinc-100"
                            data-testid="affiliate-pw-current" />
                    </div>
                    <div>
                        <Label className="text-xs text-zinc-400">New password</Label>
                        <div className="relative">
                            <Input type={show ? 'text' : 'password'} value={next}
                                onChange={(e) => setNext(e.target.value)}
                                className="mt-1 bg-zinc-900 border-zinc-700 text-zinc-100 pr-9"
                                data-testid="affiliate-pw-new" />
                            <button type="button" onClick={() => setShow(s => !s)}
                                className="absolute right-2 top-2 text-zinc-500 hover:text-zinc-300">
                                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}
                        className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800">Cancel</Button>
                    <Button onClick={submit} disabled={busy}
                        className="bg-amber-500 hover:bg-amber-400 text-zinc-900"
                        data-testid="affiliate-pw-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update password'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
